import React from 'react';
import { Prompt } from 'react-router';
import { useTranslation } from 'services/localization/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import Container from 'components/Container';
import HasFeature from 'components/features/HasFeature';
import Skidata from './Skidata';
import Tekso from './Tekso';
import Oskar from './Oskar';
import ApiKeys from './ApiKeys';
import Webhooks from './Webhooks';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import { Routes } from 'routing';
import AnalyticsTagInput from './components/AnalyticsTagInput';
import { useForm, Controller } from 'react-hook-form';
import { api } from 'common/frontend/api';
import { GoogleAnalytics, ShopPlugins } from 'common/types';
import SaveButton from 'common/components/SaveButton';
import {
	isValidUAAnalyticsTag,
	isValidGA4AnalyticsTag,
	isValidGtmTag,
} from 'common/utils/validation';
import CloudPos from './CloudPos';
import { Typography, Grid } from '@mui/material';
import ContainerBox from 'common/components/layout/ContainerBox';
import AddOnPromoBlock from 'components/features/promos/AddOnPromoBlock';

type FormValues = {
	googleAnalytics: GoogleAnalytics;
	cloudPos?: ShopPlugins['cloudPos'];
	teksoPos?: ShopPlugins['teksoPos'];
};

const Integrations = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const shopInfo = useSelector(ShopSelectors.activeShopInfo)!;
	const publicInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const form = useForm<FormValues>({
		defaultValues: {
			googleAnalytics: publicInfo.plugins?.googleAnalytics ?? {},
			cloudPos: shopInfo.plugins?.cloudPos,
			teksoPos: shopInfo.plugins?.teksoPos,
		},
		mode: 'onChange',
	});

	const { isDirty, isSubmitting, isValid, dirtyFields } = form.formState;

	const isValidAnalyticsTags = (value: GoogleAnalytics) => {
		const gaTags = value;
		if (!gaTags) return true;
		const validUATags = !gaTags.UA ? true : gaTags.UA.every(isValidUAAnalyticsTag);
		const validGA4Tags = !gaTags.GA4 ? true : gaTags.GA4.every(isValidGA4AnalyticsTag);
		const validGTMTags = !gaTags.GTM ? true : gaTags.GTM.every(isValidGtmTag);

		return validUATags && validGA4Tags && validGTMTags;
	};

	const onSubmit = async (data: FormValues) => {
		try {
			if (!!dirtyFields.googleAnalytics) {
				await api().shops.doc(shopId).public.update({
					'plugins.googleAnalytics': data.googleAnalytics,
				});
			}
			if (!!dirtyFields.cloudPos || !!dirtyFields.teksoPos) {
				await api()
					.shops.doc(shopId)
					.update({
						...(!!dirtyFields.cloudPos && { 'plugins.cloudPos': data.cloudPos }),
						...(!!dirtyFields.teksoPos && { 'plugins.teksoPos': data.teksoPos }),
					});
			}
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.integrations.pageTitle', 'Integrations')}
				rightContent={
					<SaveButton
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						onClick={form.handleSubmit(onSubmit)}
						t={t}
					/>
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={{ to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }}
				quickLinks={[]}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				<HasFeature requiredFeature="SKIDATA">
					<Skidata />
				</HasFeature>
				<HasFeature requiredFeature="TEKSO_POS">
					<Controller
						control={form.control}
						name="teksoPos"
						render={(renderProps) => (
							<Tekso
								teksoData={renderProps.field.value}
								onTeksoDataChange={renderProps.field.onChange}
							/>
						)}
					/>
				</HasFeature>
				<HasFeature requiredFeature="CLOUD_POS">
					<Controller
						control={form.control}
						name="cloudPos"
						render={(renderProps) => (
							<CloudPos
								cloudPosData={renderProps.field.value}
								onCloudPosDataChange={renderProps.field.onChange}
							/>
						)}
					/>
				</HasFeature>
				<HasFeature requiredFeature="OSKAR_POS">
					<Oskar />
				</HasFeature>
				<HasFeature
					requiredFeature="GOOGLE_ANALYTICS"
					fallback={(failedChecks) => {
						return !failedChecks.user ? <AddOnPromoBlock addOn="GOOGLE_ANALYTICS" /> : null;
					}}
				>
					<ContainerBox>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Typography variant="h6">
									{t('Analytics.connectAnalytics', 'Connect to Google Analytics')}
								</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<Controller
									control={form.control}
									name="googleAnalytics"
									rules={{
										validate: (value) => {
											return isValidAnalyticsTags(value);
										},
									}}
									render={(renderProps) => (
										<AnalyticsTagInput
											gaTags={renderProps.field.value}
											setGaTags={renderProps.field.onChange}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</ContainerBox>
				</HasFeature>

				<HasFeature requiredFeature="BETA_API">
					<ApiKeys />
				</HasFeature>
				<HasFeature requiredFeature="WEBHOOKS">
					<Webhooks />
				</HasFeature>
			</Container>
		</>
	);
};

export default Integrations;
